import { useTranslation } from 'react-i18next';

import { Flex, Heading } from '@amzn/storm-ui';
import { Form } from '@amzn/storm-ui-column-form-kit';

import { ErrorDisplay } from '../../../Shared/components/ErrorDisplay/ErrorDisplay';
import FormInputField from '../../../Shared/components/FormInputField';
import { UserRoles, isAdminRole } from '../../../Shared/constants/userRoles';
import { ApiError } from '../../../Shared/interfaces/ApiError';
import { CustomButton } from '../../../UiComponentsLibrary/CustomButton';
import {
    CustomButtonColor
} from '../../../UiComponentsLibrary/CustomButton/constants/CustomButtonColor';
import { CustomCard } from '../../../UiComponentsLibrary/CustomCard';
import { useEditUser } from '../../hooks/useEditUser';
import { useGetCategories } from '../../hooks/useGetCategories';
import { EditUserProps } from '../../interfaces/userInterfaces';
import CategoryDropDown from '../CategoryDropDown';
import OrgNameDropDown from '../OrgNameDropDown';
import UserRoleDropDown from '../UserRoleDropDown';
import PrivilegedModeDropDown from '../PrivilegedModeDropDown';

const EditUser = (props: EditUserProps) => {
  const {
    userFormData: { userRole, category, privilegedMode, orgName },
    editUserMutation,
    userId,
  } = props;

  const isUserRoleAccountExecutive = userRole === UserRoles.ACCOUNT_EXECUTIVE;
  const { t } = useTranslation();
  const {
    data: categories,
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useGetCategories(isUserRoleAccountExecutive);
  const {
    inputFields,
    fieldErrors,
    onCancel,
    handleSubmit,
    setUserRole,
    setCategory,
    setPrivilegedMode,
    setOrgName,
  } = useEditUser(props);

  return (
    <CustomCard id="edit-user-card" title={t('edit-user-label')}>
      <Heading id="edit-user-info-message" renderAs="h5">
        {t('edit-user-info-message', {
          userId: userId,
        })}
      </Heading>
      {editUserMutation.isError && (
        <ErrorDisplay error={editUserMutation.error as ApiError} />
      )}
      <Form id="edit-user-form">
        {inputFields.map((field) => (
          <FormInputField
            key={field.id}
            id={field.id}
            label={field.label}
              value={field.value}
            onChange={field.onChange}
            placeholder={field.placeholder}
            error={fieldErrors[field.id as keyof typeof fieldErrors]}
          />
        ))}
        <UserRoleDropDown
          userRole={userRole}
          setUserRole={setUserRole}
          error={fieldErrors.userRole}
        />
        {isUserRoleAccountExecutive && (
          <>
            <OrgNameDropDown
              orgName={orgName}
              setOrgName={setOrgName}
              fieldError={fieldErrors.orgName}
            />
            <CategoryDropDown
              categories={categories?.categories || []}
              category={category}
              setCategory={setCategory}
              categoriesLoading={categoriesLoading}
              fieldError={fieldErrors.category}
              categoriesFetchingError={categoriesError}
            />
          </>
        )}
        {!isAdminRole(userRole) && (
          <PrivilegedModeDropDown
            privilegedMode={privilegedMode}
            setPrivilegedMode={setPrivilegedMode}
          />
        )}
      </Form>
      <Flex justifyContent="flex-end" padding="xsmall" columnGap="small">
        <CustomButton
          id="edit-user-cancel-button"
          text={t('cancel-button')}
          backgroundColor={CustomButtonColor.cancel}
          handleOnClickCb={onCancel}
        />
        <CustomButton
          id="edit-user-submit-button"
          text={
            editUserMutation.isLoading
              ? t('edit-user-submit-button-loading-text')
              : t('submit-button')
          }
          backgroundColor={CustomButtonColor.submit}
          handleOnClickCb={handleSubmit}
        />
      </Flex>
    </CustomCard>
  );
};
export default EditUser;
